import type { ReactNode } from 'react';
import type { ResourceState } from '../../hooks/useApiResource';
import { describeError } from '../../api/client';
import { EmptyState, ErrorState, LoadingState } from '../StateMessage';
import styles from './FactSection.module.css';

/**
 * The shell every employee-fact section sits in, and the one place their
 * loading, empty and failure states are decided.
 *
 * Four sections arriving at once is four chances to invent four slightly
 * different ways of saying "this did not load". They share this component
 * instead, so a reader learns the pattern once: a heading, a factual
 * summary line, a note about how the section should be read, then the
 * records — or one clearly-worded state where the records would be.
 *
 * ---------------------------------------------------------------------
 * ON EMPTY STATES
 *
 * `empty` is required, and it is deliberately two fields rather than one
 * string. "No skills recorded" is a statement about the records; it is not
 * a statement about the person, and read alone it can easily be taken for
 * one. The `detail` line exists to close that gap in words, every time —
 * absence of a record is not evidence of absence, and it is certainly not
 * a finding about performance (docs/PRODUCT_CONSTITUTION.md).
 *
 * A section whose request FAILED must never fall through to the empty
 * state: "we could not load this" and "there is nothing here" are
 * different facts, and quietly showing the second when the first is true
 * would be the UI asserting something it does not know.
 *
 * The `summary` line is suppressed alongside the records, for both states.
 * "0 records, most recent first" above an empty-state message is noise at
 * best, and at worst it counts a person's zero and puts a number where the
 * honest answer is a sentence.
 * ---------------------------------------------------------------------
 */
export interface FactSectionProps<T> {
  id: string;
  title: string;
  state: ResourceState<T>;
  loadingLabel: string;
  errorTitle: string;
  /** Whether the loaded response carries no records at all. */
  isEmpty: (data: T) => boolean;
  empty: { title: string; detail: string };
  /** A factual line about what is being shown — counts and the as-of point,
   * never an evaluation of the person. */
  summary?: (data: T) => ReactNode;
  /** How this section is meant to be read: what the records are, and what
   * must not be inferred from them. */
  note?: ReactNode;
  children: (data: T) => ReactNode;
}

export function FactSection<T>({
  id,
  title,
  state,
  loadingLabel,
  errorTitle,
  isEmpty,
  empty,
  summary,
  note,
  children,
}: FactSectionProps<T>) {
  const headingId = `${id}-heading`;

  // Branching on `status` rather than on whether the data is null: `null` is
  // a legitimate loaded value for some sections (a role fit for an employee
  // with no current role is loaded, and empty, and null), and treating it as
  // "not loaded yet" left such a section rendering nothing at all.
  const body =
    state.status === 'loading' ? (
      <LoadingState label={loadingLabel} />
    ) : state.status === 'failed' ? (
      <ErrorState title={errorTitle} detail={describeError(state.error)} />
    ) : isEmpty(state.data) ? (
      <EmptyState title={empty.title} detail={empty.detail} />
    ) : (
      children(state.data)
    );

  const showsRecords = state.status === 'loaded' && !isEmpty(state.data);

  return (
    <section className={styles.section} id={id} aria-labelledby={headingId}>
      <header className={styles.header}>
        <h2 id={headingId} className={styles.heading}>
          {title}
        </h2>
        {showsRecords && summary ? <p className={styles.summary}>{summary(state.data)}</p> : null}
      </header>

      {showsRecords && note ? <p className={styles.note}>{note}</p> : null}

      {body}
    </section>
  );
}
